import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Zap, X, ExternalLink, Calendar, Clock, Search, ChevronRight, ChevronLeft } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import AppLayout from './AppLayout';

// Simple color schemes that work without external dependencies
const getSimpleColors = (accentColor) => {
  const colorMap = {
    maroon: {
      primary: '#b65a1b',
      accent: '#dd6b20',
      light: '#fed7aa',
      border: '#fdba74'
    },
    teal: {
      primary: '#0891b2',
      accent: '#06b6d4',
      light: '#a7f3d0',
      border: '#67e8f9'
    },
    indigo: {
      primary: '#4f46e5',
      accent: '#6366f1',
      light: '#c7d2fe',
      border: '#a5b4fc'
    },
    emerald: {
      primary: '#059669',
      accent: '#10b981',
      light: '#a7f3d0',
      border: '#6ee7b7'
    }
  };

  return colorMap[accentColor] || colorMap.indigo;
};


// Reusable card component
const ResourceCard = ({ children, className = "", colors }) => (
  <div
    className={`bg-white border rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden ${className}`}
    style={{ borderColor: colors.border }}
    tabIndex="0"
  >
    <div className="p-6">
      {children}
    </div>
  </div>
);

// Reusable button component
const PrimaryButton = ({ children, onClick, className = "", ariaLabel, colors, disabled = false }) => (
  <button
    className={`px-4 py-2 rounded-full flex items-center text-white focus:ring-2 focus:outline-none transition-colors duration-300 ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
    onClick={onClick}
    aria-label={ariaLabel}
    disabled={disabled}
    style={{
      backgroundColor: disabled ? '#6b7280' : colors.primary,
      boxShadow: `0 0 0 2px ${colors.primary}30`
    }}
  >
    {children}
  </button>
);

// External link component
const ExternalLinkButton = ({ href, children, colors }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="hover:text-opacity-80 transition-colors duration-300 flex items-center focus:outline-none focus:underline"
    style={{ color: colors.primary }}
    aria-label={`Visit ${children} (opens in new tab)`}
  >
    {children} <ExternalLink className="ml-1 h-4 w-4" />
  </a>
);

// Custom hook for data fetching
const useResourceData = () => {
  const [data] = useState({
    courses: [],
    webinars: [],
    internships: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const error = null;

  // No resource catalog is connected yet. Do not substitute sample records.
  useEffect(() => { setIsLoading(false); }, []);

  return { data, isLoading, error };
};

// Pagination component
const Pagination = ({ currentPage, totalPages, onPageChange, colors }) => {
  return (
    <div className="flex items-center justify-center mt-6 space-x-2" role="navigation" aria-label="Pagination">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2"
        style={{ color: colors.primary }}
        aria-label="Previous page"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <span className="text-sm text-gray-700">
        Page {currentPage} of {totalPages}
      </span>
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2"
        style={{ color: colors.primary }}
        aria-label="Next page"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
};

// Main component
const ResourcesDashboard = () => {
  const { userData } = useUser();
  const { data, isLoading, error } = useResourceData();

  // Simple accent color detection
  const [currentAccentColor, setCurrentAccentColor] = useState('indigo');

  useEffect(() => {
    // Try to detect accent color from userData or default to indigo
    const detectedColor = userData?.accentColor || userData?.settings?.accentColor || 'indigo';
    setCurrentAccentColor(detectedColor);
  }, [userData]);

  const colors = getSimpleColors(currentAccentColor);

  // Local state
  const [activeSection, setActiveSection] = useState('courses');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState('default');
  const [filterOption, setFilterOption] = useState('all');

  const ITEMS_PER_PAGE = 4;


  useEffect(() => {
    setCurrentPage(1);
  }, [activeSection]);

  const filteredData = useMemo(() => {
    let result = [...data[activeSection]];

    if (searchTerm) {
      const termLower = searchTerm.toLowerCase();
      result = result.filter(item => {
        return Object.values(item).some(
          value => typeof value === 'string' && value.toLowerCase().includes(termLower)
        );
      });
    }

    if (filterOption !== 'all') {
      switch (activeSection) {
        case 'courses':
          if (filterOption === 'inProgress') {
            result = result.filter(course => course.progress > 0 && course.progress < 100);
          } else if (filterOption === 'completed') {
            result = result.filter(course => course.progress === 100);
          } else if (filterOption === 'notStarted') {
            result = result.filter(course => course.progress === 0);
          }
          break;
        case 'webinars':
          result = result.filter(webinar => webinar.category === filterOption);
          break;
        case 'internships':
          result = result.filter(internship => internship.type === filterOption);
          break;
      }
    }

    if (sortOption !== 'default') {
      switch (activeSection) {
        case 'courses':
          if (sortOption === 'progressAsc') {
            result.sort((a, b) => a.progress - b.progress);
          } else if (sortOption === 'progressDesc') {
            result.sort((a, b) => b.progress - a.progress);
          } else if (sortOption === 'titleAsc') {
            result.sort((a, b) => a.title.localeCompare(b.title));
          } else if (sortOption === 'dateDesc') {
            result.sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated));
          }
          break;
        case 'webinars':
          if (sortOption === 'dateAsc') {
            result.sort((a, b) => new Date(a.date) - new Date(b.date));
          } else if (sortOption === 'dateDesc') {
            result.sort((a, b) => new Date(b.date) - new Date(a.date));
          } else if (sortOption === 'titleAsc') {
            result.sort((a, b) => a.title.localeCompare(b.title));
          }
          break;
        case 'internships':
          if (sortOption === 'deadlineAsc') {
            result.sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
          } else if (sortOption === 'deadlineDesc') {
            result.sort((a, b) => new Date(b.deadline) - new Date(a.deadline));
          } else if (sortOption === 'titleAsc') {
            result.sort((a, b) => a.title.localeCompare(b.title));
          }
          break;
      }
    }

    return result;
  }, [data, activeSection, searchTerm, sortOption, filterOption]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / ITEMS_PER_PAGE));
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredData, currentPage]);

  const getFilterOptions = useCallback(() => {
    switch (activeSection) {
      case 'courses':
        return [
          { value: 'all', label: 'All Courses' },
          { value: 'inProgress', label: 'In Progress' },
          { value: 'completed', label: 'Completed' },
          { value: 'notStarted', label: 'Not Started' }
        ];
      case 'webinars':
        return [
          { value: 'all', label: 'All Categories' },
          { value: 'Ethics', label: 'Ethics' },
          { value: 'Technology', label: 'Technology' },
          { value: 'Communication', label: 'Communication' },
          { value: 'Clinical', label: 'Clinical' }
        ];
      case 'internships':
        return [
          { value: 'all', label: 'All Types' },
          { value: 'Research', label: 'Research' },
          { value: 'Clinical', label: 'Clinical' },
          { value: 'Administration', label: 'Administration' },
          { value: 'Public Health', label: 'Public Health' }
        ];
      default:
        return [{ value: 'all', label: 'All' }];
    }
  }, [activeSection]);

  const getSortOptions = useCallback(() => {
    switch (activeSection) {
      case 'courses':
        return [
          { value: 'default', label: 'Default' },
          { value: 'progressDesc', label: 'Progress (High to Low)' },
          { value: 'progressAsc', label: 'Progress (Low to High)' },
          { value: 'titleAsc', label: 'Title (A-Z)' },
          { value: 'dateDesc', label: 'Recent Updates' }
        ];
      case 'webinars':
        return [
          { value: 'default', label: 'Default' },
          { value: 'dateAsc', label: 'Date (Nearest)' },
          { value: 'dateDesc', label: 'Date (Latest)' },
          { value: 'titleAsc', label: 'Title (A-Z)' }
        ];
      case 'internships':
        return [
          { value: 'default', label: 'Default' },
          { value: 'deadlineAsc', label: 'Deadline (Nearest)' },
          { value: 'deadlineDesc', label: 'Deadline (Latest)' },
          { value: 'titleAsc', label: 'Title (A-Z)' }
        ];
      default:
        return [{ value: 'default', label: 'Default' }];
    }
  }, [activeSection]);

  const renderCourses = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" role="list" aria-label="Courses">
      {paginatedData.length > 0 ? (
        paginatedData.map(course => (
          <ResourceCard key={course.id} colors={colors} role="listitem">
            <div className="flex items-center mb-4">
              <span className="text-3xl mr-4">{course.icon}</span>
              <h3 className="text-xl font-medium" style={{ color: colors.primary }}>{course.title}</h3>
            </div>
            <div className="mb-4">
              <div className="w-full bg-gray-200 rounded-full h-2" role="progressbar" aria-valuenow={course.progress} aria-valuemin="0" aria-valuemax="100">
                <div className="h-2 rounded-full" style={{ width: `${course.progress}%`, backgroundColor: colors.primary }}></div>
              </div>
              <span className="text-sm mt-1 block text-gray-600">{course.progress}% Complete</span>
              <span className="text-xs text-gray-500">Last updated: {course.lastUpdated}</span>
            </div>
            <div className="flex justify-between items-center">
              <PrimaryButton
                disabled
                colors={colors}

              >
                {course.progress === 100 ? (
                  <>
                    <X className="mr-2 h-4 w-4" /> Incomplete
                  </>
                ) : (
                  <>
                    <Zap className="mr-2 h-4 w-4" /> Complete
                  </>
                )}
              </PrimaryButton>
              {course.tutorLink && (
                <ExternalLinkButton href={course.tutorLink} colors={colors}>
                  Tutors
                </ExternalLinkButton>
              )}
            </div>
          </ResourceCard>
        ))
      ) : (
        <div className="col-span-2 text-center py-8">
          <p className="text-gray-500">No courses are published. The resource catalog is not available yet.</p>
        </div>
      )}
    </div>
  );

  const renderWebinars = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" role="list" aria-label="Webinars">
      {paginatedData.length > 0 ? (
        paginatedData.map(webinar => (
          <ResourceCard key={webinar.id} colors={colors} role="listitem">
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-xl font-medium mb-2" style={{ color: colors.primary }}>{webinar.title}</h3>
              <span className="inline-block px-2 py-1 text-xs rounded" style={{ backgroundColor: colors.light, color: colors.primary }}>
                {webinar.category}
              </span>
            </div>
            <p className="text-sm mb-2 text-gray-600">Speaker: {webinar.speaker}</p>
            <div className="flex items-center mb-2 text-gray-600">
              <Calendar className="mr-2 h-4 w-4" />
              <span className="text-sm">{webinar.date}</span>
            </div>
            <div className="flex items-center mb-2 text-gray-600">
              <Clock className="mr-2 h-4 w-4" />
              <span className="text-sm">{webinar.time} ({webinar.duration})</span>
            </div>
            <PrimaryButton className="mt-2" colors={colors} disabled>
              Register
            </PrimaryButton>
          </ResourceCard>
        ))
      ) : (
        <div className="col-span-2 text-center py-8">
          <p className="text-gray-500">No webinars are published. Registration is not available yet.</p>
        </div>
      )}
    </div>
  );

  const renderInternships = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" role="list" aria-label="Internships">
      {paginatedData.length > 0 ? (
        paginatedData.map(internship => (
          <ResourceCard key={internship.id} colors={colors} role="listitem">
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-xl font-medium mb-2" style={{ color: colors.primary }}>{internship.title}</h3>
              <span className="inline-block px-2 py-1 text-xs rounded" style={{ backgroundColor: colors.light, color: colors.primary }}>
                {internship.type}
              </span>
            </div>
            <p className="text-sm mb-2 text-gray-600">{internship.location}</p>
            <div className="flex items-center mb-2 text-gray-600">
              <Calendar className="mr-2 h-4 w-4" />
              <span className="text-sm">Deadline: {internship.deadline}</span>
            </div>
            <div className="flex items-center mb-2 text-gray-600">
              <Clock className="mr-2 h-4 w-4" />
              <span className="text-sm">Duration: {internship.duration}</span>
            </div>
            {internship.internshipLink && (
              <a
                href={internship.internshipLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 text-white px-4 py-2 rounded-full transition-colors duration-300 inline-flex items-center focus:outline-none focus:ring-2"
                style={{ backgroundColor: colors.primary }}
              >
                Apply Now <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            )}
          </ResourceCard>
        ))
      ) : (
        <div className="col-span-2 text-center py-8">
          <p className="text-gray-500">No opportunities are published. This catalog is not available yet.</p>
        </div>
      )}
    </div>
  );

  return (
    <AppLayout activeTab="resources">
      <div className="bg-cream-50 rounded-lg shadow-md p-8" style={{ backgroundColor: "#FFF8F0" }}>
        <h2 className="text-3xl font-bold mb-6" style={{ color: colors.primary }}>Your Learning Resources</h2>

        <div className="flex mb-6 space-x-4 overflow-x-auto pb-2" role="tablist" aria-label="Resource categories">
          {['Courses', 'Webinars', 'Internships'].map(section => (
            <button
              key={section}
              className="px-6 py-3 rounded-full text-sm font-medium transition-colors duration-300 focus:outline-none focus:ring-2"
              onClick={() => setActiveSection(section.toLowerCase())}
              role="tab"
              style={{
                backgroundColor: activeSection.toLowerCase() === section.toLowerCase()
                  ? colors.primary
                  : 'white',
                color: activeSection.toLowerCase() === section.toLowerCase()
                  ? 'white'
                  : '#374151',
                border: activeSection.toLowerCase() === section.toLowerCase()
                  ? 'none'
                  : '1px solid #E5E7EB'
              }}
            >
              {section}
            </button>
          ))}
        </div>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 space-y-4 md:space-y-0">
          <div className="relative w-full md:w-1/3">
            <input
              type="text"
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-full w-full focus:outline-none focus:ring-2"
              placeholder={`Search ${activeSection}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                borderColor: '#D1D5DB'
              }}
            />
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          </div>

          <div className="flex space-x-4 w-full md:w-auto">
            <select
              className="border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 bg-white"
              value={filterOption}
              onChange={(e) => setFilterOption(e.target.value)}
            >
              {getFilterOptions().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              className="border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 bg-white"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              {getSortOptions().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-pulse flex flex-col items-center">
              <div className="h-12 w-12 rounded-full mb-4" style={{ backgroundColor: colors.light }}></div>
              <div className="h-4 w-32 rounded mb-2" style={{ backgroundColor: colors.light }}></div>
              <div className="h-3 w-24 rounded" style={{ backgroundColor: colors.light }}></div>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" role="alert">
            <p className="font-medium">Error loading resources</p>
            <p className="text-sm">{error}</p>
            <button
              className="mt-2 text-sm underline focus:outline-none"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="mt-6">
            {activeSection === 'courses' && renderCourses()}
            {activeSection === 'webinars' && renderWebinars()}
            {activeSection === 'internships' && renderInternships()}

            {filteredData.length > ITEMS_PER_PAGE && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                colors={colors}
              />
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default ResourcesDashboard;
